import { useEffect, useRef, useState } from 'react';
import { useIsDesktop } from '../onboarding/useIsDesktop';
import { PricingHero } from './PricingHero';
import type { OnboardingSlide } from '../onboarding/onboardingSlides';
import { PricingDesktop } from './PricingDesktop';
import { PricingComparativa } from './PricingComparativa';
import { PlanCarousel } from './PlanCarousel';
import {
  BillingToggle,
  CloseButton,
  HelpButton,
  PlanSelector,
  SubscribeButton,
} from './PricingControls';
import { FOOTER_SCRIM, SHEET_GRADIENT } from './pricingStyles';
import { type PricingPlanContent, type PricingPlanKey } from './pricingContent';
import type { PricingContextCopy } from './pricingContexts';

/**
 * Catálogo de planes del paywall — rediseño de Figma "Pricing" (node 2044:448).
 * Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales (rediseño) y ADR-2026-07-28-desktop-3-columnas-vez-selector (adaptación a desktop).
 *
 * **Mobile** (`<lg`): réplica del diseño. Hero arriba, sheet azul con selector
 * segmentado y UNA card por vez, y CTA fijo al pie. El cerrar y el switch de
 * facturación quedan anclados arriba y el CTA abajo: el único que scrollea es el
 * contenido del sheet (ADR-2026-07-28-chrome-paywall-va-anclado-overlay).
 *
 * **Desktop** (`≥lg`): el selector segmentado no tiene sentido con espacio para
 * los 3 planes a la vez, así que se reemplaza por 3 columnas con su propio CTA
 * —el patrón de todas las referencias de paywall desktop (Krea, Pitch, Craft,
 * Sana)— manteniendo el hero como banda superior y el mismo lenguaje visual.
 *
 * **`variant="sheet"`** (ADR-2026-07-29-presentacion-bottom-sheet-mobile-tocar): en mobile el mismo catálogo se presenta como
 * bottom sheet —scrim, esquinas redondeadas, entrada slide-up y arrastre hacia
 * abajo para cerrar— sin tocar nada en desktop, donde sigue siendo el overlay de
 * siempre. Se usa cuando el catálogo **interrumpe** un flujo (el upsell del alta)
 * en vez de ser la pantalla a la que el usuario entró.
 *
 * Todo lo de acá es presentacional: `Paywall` calcula precios, moneda, descuento
 * y estados, y los pasa ya resueltos en `plans`.
 */

/** Duración (ms) de la entrada/salida del bottom sheet. */
const SHEET_ANIM_MS = 300;

/** Arrastre vertical (px) a partir del cual soltar cierra el sheet. */
const SHEET_DISMISS_PX = 100;

/** Un plan ya resuelto por `Paywall`: copy + precios formateados + estado. */
export interface PlanView {
  key: PricingPlanKey;
  content: PricingPlanContent;
  credits: number;
  generates: string[];
  /** Precio vigente ya formateado. */
  price: string;
  /** `por mes`, o `el primer mes` cuando hay descuento. */
  period: string;
  /**
   * Equivalente anual ya formateado. **Placeholder** derivado del mensual: no hay
   * plan anual en el backend, sólo lo ven cuentas `dev_tester` (ADR-2026-07-28-switch-mensual-anual-visual-solo).
   */
  annual: { total: string; perMonth: string };
  /** Muestra "IVA incluido" bajo el precio. Sólo cuentas argentinas (precios en ARS). */
  ivaIncluido: boolean;
  strikePrice?: string;
  discountLabel?: string;
  isCurrent: boolean;
  ctaLabel: string;
  ctaDisabled: boolean;
  /**
   * Acción secundaria bajo el CTA — hoy sólo "Cancelar suscripción" en la card
   * del plan vigente. Es la única entrada al flujo de baja, así que no puede
   * perderse en el rediseño.
   */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export interface PricingCatalogProps {
  plans: PlanView[];
  selected: PricingPlanKey;
  onSelect: (key: PricingPlanKey) => void;
  onSubscribe: (key: PricingPlanKey) => void;
  processing: boolean;
  onClose: () => void;
  /** Slider de tiers de Ultra. */
  ultraTier: { index: number; count: number; onChange: (i: number) => void };
  /** El switch Mensual/Anual sólo se muestra a cuentas `dev_tester` (ADR-2026-07-28-switch-mensual-anual-visual-solo). */
  showBillingToggle: boolean;
  annual: boolean;
  onAnnualChange: (annual: boolean) => void;
  /** Aviso al pie. */
  note?: React.ReactNode;
  /**
   * Ayuda por WhatsApp: pinta el botón flotante de arriba a la derecha. El
   * contenedor decide qué hace (link directo en mobile, modal con QR en desktop).
   */
  onHelp?: () => void;
  /**
   * Error de la operación. Se muestra DENTRO del overlay a propósito: el
   * catálogo tapa la página, así que un banner por debajo no se vería.
   */
  error?: string | null;
  /**
   * Presentación. `screen` (default) es el overlay full-screen de
   * `/subscription`; `sheet` lo convierte en bottom sheet **sólo en mobile**
   * (desktop queda idéntico a `screen`). Ver ADR-2026-07-29-presentacion-bottom-sheet-mobile-tocar.
   *
   * `embedded` (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil, rehecho en ADR-2026-07-31-embebido-deja-ser-modal-scrim) lo saca del overlay **y cambia de
   * layout**: los tres planes uno al lado del otro (`PricingComparativa`), sin
   * carrusel, sin segmentado de planes y sin cruz de cerrar. Lo usa
   * Configuración → Plan, donde el usuario entró a comparar y tiene la pantalla
   * entera, no a ser interrumpido.
   *
   * `comparativa` (ADR-2026-07-31-desktop-pasa-comparativo-tres-columnas) es esa misma tabla de tres columnas pero **como
   * página**: fondo, ancho máximo y una vuelta arriba. Es lo que renderiza
   * `/subscription` en desktop. En mobile cae a `screen`, que ahí sigue siendo la
   * presentación buena.
   */
  variant?: 'screen' | 'sheet' | 'embedded' | 'comparativa';
  /**
   * Desvanece el catálogo al montarse, con la misma curva que el cambio de página
   * (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). Lo piden los **overlays**, que aparecen sobre una pantalla que se
   * queda donde está.
   *
   * Apagado por defecto porque `/subscription` es una ruta dentro de
   * `DashboardLayout` y ya se desvanece con el fade global: sumarle éste anidaría
   * dos animaciones de 680ms, que se multiplican.
   *
   * No afecta al bottom sheet de mobile, que entra deslizándose —ese movimiento es
   * la contracara del gesto de arrastrar para cerrar—.
   */
  animateIn?: boolean;
  /**
   * Acción de descarte bajo el CTA ("Omitir por ahora"), con el mismo estilo
   * ghost que "Cancelar suscripción". Sólo tiene sentido cuando el catálogo
   * interrumpe un flujo y hay algo que continuar al cerrarlo.
   */
  /**
   * Slides del carrusel lateral. Por defecto el set general; los callers que conocen
   * el rubro de la empresa mandan el suyo
   * (ADR-2026-08-03-catalogo-planes-usa-slides-del-rubro).
   */
  slides?: OnboardingSlide[];
  skipLabel?: string;
  onSkip?: () => void;
  /**
   * Copy del contexto desde el que se abrió el catálogo (ADR-2026-07-30-copy-catalogo-segun-contexto-abrio). En mobile
   * reemplaza el título y la bajada del carrusel y se mantiene fijo a lo largo de
   * las 4 imágenes; en desktop va en el panel derecho, arriba del switch
   * Mensual/Anual. Sin esto el catálogo queda como estaba.
   */
  contextCopy?: PricingContextCopy;
}

export function PricingCatalog({
  plans,
  selected,
  onSelect,
  onSubscribe,
  processing,
  onClose,
  ultraTier,
  showBillingToggle,
  annual,
  onAnnualChange,
  note,
  error,
  onHelp,
  variant = 'screen',
  animateIn = false,
  slides,
  skipLabel,
  onSkip,
  contextCopy,
}: PricingCatalogProps) {
  const current = plans.find((p) => p.key === selected) ?? plans[0];
  const planKeys = plans.map((p) => p.key);
  const isDesktop = useIsDesktop();
  // El tratamiento de sheet es EXCLUSIVO de mobile: en desktop `variant="sheet"`
  // renderiza exactamente el mismo overlay que `screen` (pedido del usuario:
  // "formato normal que ya utilizamos en desktop").
  // `embedded` nunca es sheet: no hay nada que arrastrar hacia abajo para cerrar
  // cuando el catálogo es parte de la página.
  const isSheet = variant === 'sheet' && !isDesktop;
  const { entered, dragY, dismiss, sheetHandlers, scrollRef } = useBottomSheet(
    isSheet,
    onClose,
  );

  // Los tres planes en fila (ADR-2026-07-31-embebido-deja-ser-modal-scrim). Dos envoltorios para el mismo contenido:
  //
  // · `embedded` — sin nada alrededor: lo enmarca la pantalla que lo monta
  //   (Configuración → Plan), que además pone su propia vuelta.
  // · `comparativa` — como PÁGINA (ADR-2026-07-31-desktop-pasa-comparativo-tres-columnas): fondo, ancho máximo y un "Volver"
  //   propio, porque `/subscription` va portaleada a `<body>` y tapa la sidebar;
  //   sin esa salida el usuario quedaría encerrado. Sólo en desktop: abajo de
  //   1024px tres columnas se apilan en una lista larguísima, y ahí la pantalla
  //   oscura con su carrusel sigue siendo la buena.
  //
  // Van ANTES del early return de desktop y después de todos los hooks.
  if (variant === 'comparativa' && isDesktop) {
    return (
      <div
        className={`pricing-cursor-scope fixed inset-0 z-[200] overflow-y-auto bg-surface-content ${
          animateIn ? 'page-fade-in' : ''
        }`}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10">
          <button
            type="button"
            onClick={onClose}
            className="mb-6 text-[13.5px] font-medium text-neutral-500 transition-colors hover:text-brand-black/90"
          >
            ← Volver
          </button>
          <PricingComparativa
            plans={plans}
            selected={selected}
            onSelect={onSelect}
            onSubscribe={onSubscribe}
            processing={processing}
            onClose={onClose}
            ultraTier={ultraTier}
            showBillingToggle={showBillingToggle}
            annual={annual}
            onAnnualChange={onAnnualChange}
            note={note}
            error={error}
            contextCopy={contextCopy}
          />
        </div>
      </div>
    );
  }

  if (variant === 'embedded') {
    return (
      <PricingComparativa
        plans={plans}
        selected={selected}
        onSelect={onSelect}
        onSubscribe={onSubscribe}
        processing={processing}
        onClose={onClose}
        ultraTier={ultraTier}
        showBillingToggle={showBillingToggle}
        annual={annual}
        onAnnualChange={onAnnualChange}
        note={note}
        error={error}
        contextCopy={contextCopy}
      />
    );
  }

  // Desktop tiene su propio layout: modal de dos paneles (ADR-2026-07-29-desktop-paywall-modal-dos-paneles, supersede las
  // 3 columnas de ADR-2026-07-28-desktop-3-columnas-vez-selector). Va como early return DESPUÉS de todos los hooks —
  // antes rompería las reglas de hooks— y se elige por JS, no con clases `lg:`,
  // porque los dos árboles son distintos: tenerlos a la vez en el DOM montaría
  // DOS carruseles, con sus <video> reproduciéndose en paralelo.
  if (isDesktop) {
    return (
      <PricingDesktop
        slides={slides}
        plans={plans}
        selected={selected}
        onSelect={onSelect}
        onSubscribe={onSubscribe}
        processing={processing}
        onClose={onClose}
        ultraTier={ultraTier}
        showBillingToggle={showBillingToggle}
        annual={annual}
        onAnnualChange={onAnnualChange}
        note={note}
        error={error}
        onHelp={onHelp}
        skipLabel={skipLabel}
        onSkip={onSkip}
        contextCopy={contextCopy}
        animateIn={animateIn}
        variant={variant}
      />
    );
  }

  /**
   * En sheet, TODA acción principal saca primero al sheet de pantalla y recién
   * después avisa al contenedor —incluido `onSubscribe`: el sheet interrumpe un
   * flujo, así que suscribirse también significa salir de él—. En `screen` los
   * callbacks se disparan tal cual, como siempre.
   */
  const close = () => (isSheet ? dismiss(onClose) : onClose());
  const skip = onSkip ? () => (isSheet ? dismiss(onSkip) : onSkip()) : undefined;
  const subscribe = (key: PricingPlanKey) =>
    isSheet ? dismiss(() => onSubscribe(key)) : onSubscribe(key);

  const body = (
    <>
      {/* ── Capa scrolleable: es lo ÚNICO que se mueve ── */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto w-full lg:max-w-[1120px] lg:px-8 lg:py-10">
          <div className="lg:overflow-hidden lg:rounded-[28px]">
            {/* Con contexto, el copy del carrusel se reemplaza por el del momento
                en que se abrió el catálogo y queda fijo en las 4 imágenes. */}
            <PricingHero fixedCopy={contextCopy} slides={slides} />

            {/* Sheet azul: monta 24px por encima del hero, como en el frame. */}
            <div
              className="relative -mt-6 rounded-t-3xl px-5 py-6 lg:mt-0 lg:rounded-none lg:px-10 lg:pb-12 lg:pt-10"
              style={{ background: SHEET_GRADIENT }}
            >
              {error ? (
                <div className="mb-5 rounded-xl border border-white/25 bg-black/25 px-4 py-3 text-[13px] leading-[18px] text-white">
                  {error}
                </div>
              ) : null}

              {/* Mobile: selector segmentado + las tres cards deslizables.
                  Antes se montaba UNA sola card y el segmentado era la única forma
                  de cambiar de plan; ahora se desliza con el dedo y el segmentado
                  queda como atajo e indicador (ADR-2026-08-03-cards-de-planes-deslizables-en-mobile).
                  La animación de entrada por card se fue con el cambio: con las tres
                  montadas, el movimiento del carrusel ES la transición. */}
              <div className="flex flex-col gap-6 lg:hidden">
                <PlanSelector
                  selected={selected}
                  onSelect={onSelect}
                  planKeys={planKeys}
                />
                <PlanCarousel
                  plans={plans}
                  selected={selected}
                  onSelect={onSelect}
                  ultraTier={ultraTier}
                />
              </div>

              {note ? (
                <div className="mt-6 text-center text-[12px] leading-[17px] text-white/70">
                  {note}
                </div>
              ) : null}

              {/* Colchón para que el CTA fijo no tape el final del contenido. */}
              <div className="h-[112px] lg:hidden" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Chrome fijo: cerrar + switch de facturación ── */}
      {/* En desktop el padding lateral replica el del contenedor scrolleable
          (max-w 1120 + px-8) para que el botón de cerrar caiga EXACTAMENTE sobre
          el borde derecho del modal y no por fuera. */}
      {/* En sheet el borde superior ya está por debajo del notch (lo empuja el
          propio sheet), así que el safe-area no se suma de nuevo; el padding
          deja lugar al handle de arrastre. */}
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 z-20 px-5 lg:px-0 lg:pt-14 ${
          isSheet ? 'pt-7' : 'pt-[calc(env(safe-area-inset-top)+16px)]'
        }`}
      >
        <div className="relative mx-auto flex h-9 w-full items-center lg:max-w-[1120px] lg:px-8">
          <div className="pointer-events-auto">
            <CloseButton onClose={close} />
          </div>
          {/* El switch va centrado de forma ABSOLUTA en los dos breakpoints: si
              lo centráramos con el flujo (`mx-auto`), al ocultarlo en cuentas sin
              `dev_tester` el botón de ayuda se pegaría al de cerrar. */}
          {showBillingToggle ? (
            <div className="pointer-events-auto absolute left-1/2 -translate-x-1/2">
              <BillingToggle annual={annual} onChange={onAnnualChange} />
            </div>
          ) : null}
          {/* Ayuda: arriba a la derecha, en espejo del botón de cerrar. */}
          {onHelp ? (
            <div className="pointer-events-auto ml-auto">
              <HelpButton onHelp={onHelp} />
            </div>
          ) : null}
        </div>
      </div>

      {/* ── CTA fijo al pie (sólo mobile: en desktop cada card tiene el suyo) ── */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 px-5 pb-[calc(env(safe-area-inset-bottom)+20px)] pt-10 lg:hidden"
        style={{ background: FOOTER_SCRIM }}
      >
        <SubscribeButton
          label={current.ctaLabel}
          onClick={() => subscribe(current.key)}
          disabled={current.ctaDisabled}
          loading={processing}
        />
        {current.secondaryLabel && current.onSecondary ? (
          <button
            type="button"
            onClick={current.onSecondary}
            className="mt-2 w-full text-center text-[13px] font-[600] leading-[17px] text-white/70 underline underline-offset-2"
          >
            {current.secondaryLabel}
          </button>
        ) : null}
        {skip && skipLabel ? (
          <button
            type="button"
            onClick={skip}
            className="mt-2 w-full text-center text-[13px] font-[600] leading-[17px] text-white/70 underline underline-offset-2"
          >
            {skipLabel}
          </button>
        ) : null}
      </div>
    </>
  );

  // `pricing-cursor-scope`: manito custom sobre lo clickeable (ADR-2026-07-30-switch-opaco-sobre-panel-blanco). Aplica
  // entre 768px y el breakpoint de desktop, donde este layout todavía se ve con
  // mouse; abajo de 768 la regla no existe.
  if (!isSheet) {
    return (
      // Mismo fade opt-in que en `PricingDesktop` (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo): esta rama es el caso
      // intermedio —`variant="sheet"` por debajo del breakpoint de desktop pero por
      // encima del de mobile—, así que también puede ser un overlay.
      <div
        className={`pricing-cursor-scope fixed inset-0 z-[200] flex flex-col bg-black ${
          animateIn ? 'page-fade-in' : ''
        }`}
      >
        {body}
      </div>
    );
  }

  return (
    <div className="pricing-cursor-scope fixed inset-0 z-[200]">
      {/* Scrim: el sheet no llega al borde superior, así que abajo tiene que
          verse que hay una pantalla detrás. No cierra al tocarlo — las salidas
          son las tres pedidas: cerrar, arrastrar y "omitir". */}
      <div
        aria-hidden
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ease-out ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Planes de Atlas"
        className="absolute inset-x-0 bottom-0 flex h-[calc(100dvh-env(safe-area-inset-top)-24px)] flex-col overflow-hidden rounded-t-[20px] bg-black"
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : entered ? 'translateY(0)' : 'translateY(100%)',
          // Durante el arrastre el transform sigue al dedo: cualquier transición
          // lo haría ir por detrás y se sentiría gomoso.
          transition: dragY > 0 ? 'none' : `transform ${SHEET_ANIM_MS}ms ease-out`,
        }}
        {...sheetHandlers}
      >
        {/* Handle de arrastre, sobre el hero. Es sólo el indicador visual: el
            gesto se escucha en todo el sheet (ver `useBottomSheet`). */}
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 z-30 flex h-6 items-center justify-center"
        >
          <span className="h-[5px] w-9 rounded-full bg-white/40" />
        </span>
        {body}
      </div>
    </div>
  );
}

/**
 * Gesto y animación del bottom sheet: entrada slide-up, arrastre hacia abajo
 * para cerrar y salida animada antes de avisarle al contenedor.
 *
 * El arrastre se escucha en **todo el sheet**, no sólo en el handle: el hero
 * ocupa más de media pantalla y limitarlo a una franja de 6px de alto hubiera
 * hecho el gesto casi imposible de acertar. Para no pelearse con lo que ya vive
 * adentro sólo arranca si (a) el contenido está scrolleado arriba de todo y (b)
 * el primer tramo del movimiento es más vertical que horizontal —el carrusel
 * del hero usa el horizontal para cambiar de slide—.
 */
function useBottomSheet(active: boolean, onClose: () => void) {
  const [entered, setEntered] = useState(false);
  const [dragY, setDragY] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; on: boolean | null } | null>(null);

  useEffect(() => {
    if (!active) {
      setEntered(false);
      return;
    }
    // Doble rAF: sin él React commitea el mount y el `translateY(0)` en el mismo
    // frame y la entrada no se anima (mismo motivo que `useExitTransition`).
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, [active]);

  /** Anima la salida y recién ahí ejecuta la acción del contenedor. */
  const dismiss = (done: () => void) => {
    setEntered(false);
    setDragY(0);
    window.setTimeout(done, SHEET_ANIM_MS);
  };

  const sheetHandlers = {
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      drag.current = { x: t.clientX, y: t.clientY, on: null };
    },
    onTouchMove: (e: React.TouchEvent) => {
      const d = drag.current;
      if (!d) return;
      const t = e.touches[0];
      const dy = t.clientY - d.y;
      const dx = t.clientX - d.x;
      if (d.on === null) {
        // Todavía indeciso: esperamos a que el movimiento se defina.
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        const atTop = (scrollRef.current?.scrollTop ?? 0) <= 0;
        d.on = atTop && dy > 0 && Math.abs(dy) > Math.abs(dx);
      }
      if (!d.on) return;
      // No hace falta `preventDefault` (que además sería inútil: React escucha
      // touchmove en modo pasivo): el scroll ya está arriba de todo y
      // `overscroll-contain` corta el encadenado.
      setDragY(Math.max(0, dy));
    },
    onTouchEnd: () => {
      const shouldClose = dragY > SHEET_DISMISS_PX;
      drag.current = null;
      if (shouldClose) dismiss(onClose);
      else setDragY(0);
    },
    onTouchCancel: () => {
      drag.current = null;
      setDragY(0);
    },
  };

  return { entered, dragY, dismiss, sheetHandlers, scrollRef };
}

export default PricingCatalog;
